@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.`init`.InitKobwebContext
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import com.timacookies.components.layouts.MarkdownLayout as _com_timacookies_components_layouts_MarkdownLayout
import com.timacookies.components.layouts.PageLayout as _com_timacookies_components_layouts_PageLayout
import com.timacookies.pages.IndexPage as _com_timacookies_pages_IndexPage
import com.timacookies.pages.de.PrivacyPage as _com_timacookies_pages_de_PrivacyPage
import com.timacookies.pages.en.PrivacyPage as _com_timacookies_pages_en_PrivacyPage
import com.timacookies.pages.lang.HomePage as _com_timacookies_pages_lang_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "TIMA COOKIES"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "com.timacookies.components.layouts.MarkdownLayout",
            parentLayoutId = "com.timacookies.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.timacookies.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _com_timacookies_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "com.timacookies.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _com_timacookies_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
        ) { pageCtx -> 
            _com_timacookies_pages_IndexPage()
        }
        ctx.router.register(
            "/de/privacy",
            layoutId = "com.timacookies.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.timacookies.pages.de.initPrivacyPage(ctx)
            },
        ) { pageCtx -> 
            _com_timacookies_pages_de_PrivacyPage()
        }
        ctx.router.register(
            "/en/privacy",
            layoutId = "com.timacookies.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.timacookies.pages.en.initPrivacyPage(ctx)
            },
        ) { pageCtx -> 
            _com_timacookies_pages_en_PrivacyPage()
        }
        ctx.router.register(
            "/{lang}",
            layoutId = "com.timacookies.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.timacookies.pages.lang.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _com_timacookies_pages_lang_HomePage()
        }

        com.timacookies.initKobweb(ctx)
    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", com.timacookies.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", com.timacookies.SubheadlineTextStyle)
        ctx.theme.registerStyle("section-text", com.timacookies.SectionTextStyle)
        ctx.theme.registerStyle("footer", com.timacookies.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", com.timacookies.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("markdown", com.timacookies.components.layouts.MarkdownStyle)
        ctx.theme.registerVariant("-circle", com.timacookies.CircleButtonVariant)
        ctx.theme.registerVariant("-uncolored", com.timacookies.UncoloredButtonVariant)
        ctx.theme.registerKeyframes("fade-in", com.timacookies.FadeInAnimation)
        ctx.theme.registerKeyframes("side-menu-slide-in", com.timacookies.components.sections.SideMenuSlideInAnim)
        com.timacookies.initSiteStyles(ctx)
        com.timacookies.initColorMode(ctx)
        com.timacookies.initStyles(ctx)
        com.timacookies.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.timacookies.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
